import { Button, Col, Divider, Flex, Form, Input, Row, Select, Space, Tooltip } from "antd";
import TextArea from "antd/lib/input/TextArea";
import { useEffect, useRef, useState } from "react";
import { PessoaModel } from "../../../../models/pessoa/pessoaModel";
import { setTitle } from "../../../../redux/slices/configuracoes/configuracoesSlice";
import { atualizarPessoa, buscarPessoa, cadastrarPessoa } from "../../../../services/pessoaService";
import { useAppDispatch, useAppSelector } from "../../../../util/hooks";
import { CheckOutlined, LeftOutlined } from "@ant-design/icons";
import { buscarCep } from "../../../../services/localizacaoService";
import { buscarContatoPessoa, listarTipoContatoPessoa } from "../../../../services/contatoPessoaService";
import { setPagePessoa } from "../../../../redux/slices/pessoa/pessoaSlice";
import { MaskedInput } from "antd-mask-input";
import { formatCPFCNPJ, formatTelefone } from "../../../../util/formats";
import { mascaraCep } from "../../../../util/mascaras";
import { CategoriaPessoaModel } from "../../../../models/pessoa/categoriaPessoaModel";
import { CategoriaModel } from "../../../../models/pessoa/pessoaModel";
import { EstadoModel } from "../../../../models/localizacao/estadoModel";
import dayjs from "dayjs";
import ContatoPessoa from "../../contatoPessoa";

dayjs.locale("pt-br");

const CadastrarPage = () => {
  //estados globais
  const pessoaState = useAppSelector((state) => state.pessoa);
  const localizacaoState = useAppSelector((state) => state.localizacao);
  const contatoPessoaState = useAppSelector((state) => state.contatoPessoa);
  const persistedState = useAppSelector((state) => state.persisted);

  //hooks
  const dispatch = useAppDispatch();

  //estados locais
  const [form] = Form.useForm();
  const [inputCep, setInputCep] = useState<string>("");
  const [tipo, setTipo] = useState<boolean>(false);

  const inputInicial = useRef<any>(null);
  const inputNumero = useRef<any>(null);

  const editar = Object.values(pessoaState.entity).length !== 0;

  //apenas o perfil ADMIN pode cadastrar pessoa sem CPF/CNPJ (empresas do exterior)
  const cpfCnpjOpcional = persistedState.user.perfil === "ADMIN";

  const categoriaPessoa = pessoaState.listCategoria.filter((a: CategoriaPessoaModel) => a.id !== 4);

  useEffect(() => {
    dispatch(listarTipoContatoPessoa());
    if (editar) {
      dispatch(setTitle("EDITAR PESSOA"));
      dispatch(buscarContatoPessoa(pessoaState.entity.id));
      setInputCep(pessoaState.entity.cep?.replace(/^([\d]{5})\-?([\d]{3})/, "$1-$2"));
      onTipochange(pessoaState.entity.tipoPessoa);
      form.setFieldsValue({
        ...pessoaState.entity,
        idCategoria: pessoaState.entity.idCategoria.map(Number),
        cpfcnpj: pessoaState.entity.cpfcnpj && formatCPFCNPJ(pessoaState.entity.cpfcnpj),
        cep: pessoaState.entity.cep?.replace(/^([\d]{5})\-?([\d]{3})/, "$1-$2"),
      });
    } else {
      dispatch(setTitle("NOVO PESSOA"));
      setInputCep("");
    }
  }, [pessoaState.entity]);

  const submit = (value: any) => {
    const pessoaSubmit: PessoaModel = { ...value } as PessoaModel;
    pessoaSubmit.cep = value.cep?.replace(/[^0-9]/g, "");
    pessoaSubmit.cpfcnpj = value.cpfcnpj ? value.cpfcnpj.replace(/[^0-9]/g, "") : null;
    //categoria pessoa
    const categoria: CategoriaModel[] = [];
    value.idCategoria.forEach((v: any) => {
      var idCategoria = {
        idCategoriaPessoa: v,
      };
      categoria.push(idCategoria);
    });
    pessoaSubmit.categoriaPessoa = categoria;
    if (editar) {
      pessoaSubmit.id = pessoaState.entity.id;
      pessoaSubmit.ativo = pessoaState.entity.ativo;
      dispatch(atualizarPessoa({ value: pessoaSubmit, busca: pessoaState.busca }));
    } else {
      pessoaSubmit.ativo = true;
      dispatch(cadastrarPessoa({ value: pessoaSubmit, busca: pessoaState.busca, origem: "pessoa" }));
    }
  };

  const onBuscarCep = async () => {
    const retorno = await dispatch(buscarCep(inputCep));
    form.setFieldsValue({
      endereco: retorno.payload.logradouro,
      bairro: retorno.payload.bairro,
      cidade: retorno.payload.localidade,
      idEstado: localizacaoState.listEstado.filter((a: EstadoModel) => a.sigla === retorno.payload.uf)[0].id,
    });
    inputNumero.current.focus();
  };

  const onTipochange = (value: any) => {
    value === "PF" ? setTipo(false) : setTipo(true);
    form.setFieldValue("cpfcnpj", "");
  };

  const voltar = () => {
    dispatch(setPagePessoa("geral"));
  };

  return (
    <Flex gap={12} vertical>
      <Flex onClick={voltar} className="voltar">
        {"<"} VOLTAR
      </Flex>
      <Form form={form} layout="vertical" onFinish={submit}>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item
              label="Razão Social"
              name="razaoSocial"
              rules={[
                {
                  required: true,
                  message: "Digite a razão social",
                  validateTrigger: "onSubmit",
                },
              ]}
            >
              <Input type="text" ref={inputInicial} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Nome Fantasia"
              name="apelido"
              rules={[
                {
                  required: true,
                  message: "Digite o nome fantasia da empresa",
                  validateTrigger: "onSubmit",
                },
              ]}
            >
              <Input type="text" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={8}>
            <Form.Item
              label="Tipo Pessoa"
              name="tipoPessoa"
              rules={[
                {
                  required: true,
                  message: "Selecione o tipo pessoa",
                },
              ]}
            >
              <Select
                autoFocus
                optionFilterProp="label"
                onChange={onTipochange}
                options={[
                  {
                    value: "PJ",
                    label: "PJ",
                  },
                  {
                    value: "PF",
                    label: "PF",
                  },
                ]}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="CPF / CNPJ"
              name="cpfcnpj"
              tooltip={cpfCnpjOpcional ? "Deixe em branco para empresas do exterior, que não possuem CPF/CNPJ" : undefined}
              rules={[
                {
                  required: !cpfCnpjOpcional,
                  message: "Digite o CPF ou CNPJ da pessoa",
                  validateTrigger: "onSubmit",
                },
              ]}
            >
              {tipo ? (
                <MaskedInput defaultValue={pessoaState.entity.cpfcnpj ? formatCPFCNPJ(pessoaState.entity.cpfcnpj) : undefined} mask="00.000.000/0000-00"></MaskedInput>
              ) : (
                <MaskedInput defaultValue={pessoaState.entity.cpfcnpj ? formatCPFCNPJ(pessoaState.entity.cpfcnpj) : undefined} mask="000.000.000-00"></MaskedInput>
              )}
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label="Identidade" name="identidade">
              <Input type="text" />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label="Orgão emissor" name="orgaoEmissor">
              <Input type="text" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item label="Inscrição Estadual" name="inscricaoEstadual">
              <Input type="text" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Inscrição Municipal" name="inscricaoMunicipal">
              <Input type="text" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={8}>
            <Form.Item label="Site" name="homepage">
              <Input type="text" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="E-mail" name="email">
              <Input type="text" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="Categoria"
              name="idCategoria"
              rules={[
                {
                  required: true,
                  message: "Selecione pelo menos uma categoria",
                },
              ]}
            >
              <Select
                showSearch
                mode="multiple"
                optionFilterProp="label"
                maxTagCount="responsive"
                maxTagPlaceholder={(omittedValues) => (
                  <Tooltip
                    title={
                      <ul style={{ paddingLeft: 16, margin: 0 }}>
                        {omittedValues.map((field, index) => (
                          <li key={index}>{field.label}</li>
                        ))}
                      </ul>
                    }
                  >
                    <span>{`+ ${omittedValues.length} ...`}</span>
                  </Tooltip>
                )}
                placeholder="Selecione a(s) categoria(s)"
                options={categoriaPessoa.map((a: any) => ({
                  value: a.id,
                  label: a.denominacao,
                }))}
              />
            </Form.Item>
          </Col>
        </Row>
        <Divider orientation="left" orientationMargin="0">
          Endereço
        </Divider>
        <Row gutter={12}>
          <Col span={6}>
            <Form.Item label="CEP" name="cep">
              <MaskedInput defaultValue={pessoaState.entity.cep?.replace(/^(\d{5})(\d)/, "$1-$2") || undefined} mask={mascaraCep()} onChange={(e) => setInputCep(e.target.value)} />
            </Form.Item>
          </Col>
          <Col span={2}>
            <Form.Item label=" ">
              <Button style={{ marginLeft: -23, backgroundColor: "#f1f1f1" }} onClick={onBuscarCep}>
                Buscar
              </Button>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Endereço" name="endereco">
              <Input type="text" />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label="Número" name="numero">
              <Input type="text" ref={inputNumero} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={6}>
            <Form.Item label="Complemento" name="complemento">
              <Input type="text" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Bairro" name="bairro">
              <Input type="text" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Cidade" name="cidade">
              <Input type="text" />
            </Form.Item>
          </Col>
          <Col span={2}>
            <Form.Item label="UF" name="idEstado">
              <Select
                autoFocus
                showSearch
                optionFilterProp="label"
                loading={localizacaoState.loadingEstado}
                options={localizacaoState.listEstado.map((a: any) => ({
                  value: a.id,
                  label: a.sigla,
                }))}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={24}>
            <Form.Item label="Observação" name="observacao">
              <TextArea rows={3} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={24} className="modalFooter">
            <Space size="middle">
              <Button className="cancelButton" onClick={voltar} icon={<LeftOutlined />}>
                VOLTAR
              </Button>
              <Button htmlType="submit" icon={<CheckOutlined />} className="buttonPrimary">
                {editar ? "ATUALIZAR" : "SALVAR"}
              </Button>
            </Space>
          </Col>
        </Row>
      </Form>
      {editar && (
        <Flex vertical>
          <Divider orientation="left" orientationMargin="0">
            Contatos
          </Divider>
          <ContatoPessoa />
        </Flex>
      )}
    </Flex>
  );
};

export default CadastrarPage;
